
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type SubscriptionStatus = 'active' | 'unsubscribed' | 'bounced';

export interface NewsletterSubscription {
  id: string;
  email: string;
  name: string | null;
  status: SubscriptionStatus;
  subscribed_at: string | null;
  last_activity_at: string | null;
  preferences: Record<string, any> | null;
  source: string | null;
  metadata: Record<string, any> | null;
}

export type SubscriptionsResponse = {
  data: NewsletterSubscription[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

export type UseNewsletterSubscriptionsParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: SubscriptionStatus | 'all';
  sortBy?: 'subscribed_at' | 'email' | 'last_activity_at';
  sortOrder?: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
  source?: string;
};

export function useNewsletterSubscriptions({
  page = 1,
  pageSize = 10,
  search = "",
  status = 'all',
  sortBy = "subscribed_at",
  sortOrder = "desc",
  dateFrom,
  dateTo,
  source,
}: UseNewsletterSubscriptionsParams = {}) {
  return useQuery({
    queryKey: ["newsletter-subscriptions", page, pageSize, search, status, sortBy, sortOrder, dateFrom, dateTo, source],
    queryFn: async () => {
      try {
        // Calculate range for pagination
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        // Build query
        let countQuery = supabase
          .from("newsletter_subscriptions")
          .select("id", { count: "exact" });

        let dataQuery = supabase
          .from("newsletter_subscriptions")
          .select("*")
          .range(from, to);

        // Apply status filter if not 'all'
        if (status !== 'all') {
          countQuery = countQuery.eq("status", status);
          dataQuery = dataQuery.eq("status", status);
        }

        // Apply search if provided
        if (search && search.trim() !== "") {
          const searchTerm = `%${search.trim()}%`;
          const searchFilter = `email.ilike.${searchTerm},name.ilike.${searchTerm}`;
          
          countQuery = countQuery.or(searchFilter);
          dataQuery = dataQuery.or(searchFilter);
        }

        // Apply date range filters if provided
        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          
          countQuery = countQuery.gte("subscribed_at", fromDate.toISOString());
          dataQuery = dataQuery.gte("subscribed_at", fromDate.toISOString());
        }
        
        if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999);
          
          countQuery = countQuery.lte("subscribed_at", toDate.toISOString());
          dataQuery = dataQuery.lte("subscribed_at", toDate.toISOString());
        }
        
        // Apply source filter if provided
        if (source) {
          countQuery = countQuery.eq("source", source);
          dataQuery = dataQuery.eq("source", source);
        }

        // Apply sorting
        dataQuery = dataQuery.order(sortBy, { ascending: sortOrder === "asc" });

        // Execute both queries in parallel
        const [countResult, dataResult] = await Promise.all([
          countQuery,
          dataQuery,
        ]);

        // Handle errors
        if (countResult.error) {
          console.error("Error fetching subscription count:", countResult.error);
          throw countResult.error;
        }

        if (dataResult.error) {
          console.error("Error fetching subscriptions:", dataResult.error);
          throw dataResult.error;
        }

        // Calculate pagination values
        const total = countResult.count || 0;
        const totalPages = Math.ceil(total / pageSize) || 1; // Ensure at least 1 page

        // Make sure we cast the data to the expected type
        const subscriptionData = dataResult.data as unknown as NewsletterSubscription[];

        // Format and return the response
        return {
          data: subscriptionData,
          pagination: {
            page,
            pageSize,
            total,
            totalPages,
          },
        } as SubscriptionsResponse;
      } catch (error) {
        console.error("Error in useNewsletterSubscriptions:", error);
        toast.error("Failed to load newsletter subscriptions");
        // Return empty data with pagination info
        return {
          data: [],
          pagination: {
            page,
            pageSize,
            total: 0,
            totalPages: 0,
          },
        } as SubscriptionsResponse;
      }
    },
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: true,
  });
}
